import axios, { AxiosInstance, AxiosError } from "axios";
import {
    LoginResponse,
    Product,
    ProductsResponse,
    Category,
    CategoriesResponse,
    Client,
    ClientsResponse,
    CreateCategoryRequest,
    UpdateCategoryRequest,
    CreateProductRequest,
    UpdateProductRequest,
    CommandRequest,
    Command,
    BasketItem,
} from "../types";

const API_URL = process.env.REACT_APP_API_URL || "";

// Créer l'instance Axios
const api: AxiosInstance = axios.create({
    baseURL: API_URL,
    headers: {
        "Content-Type": "application/json",
    },
});

// Intercepteur pour ajouter le token
api.interceptors.request.use((config) => {
    const token = localStorage.getItem("token");
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Intercepteur pour gérer les erreurs
api.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
        if (error.response?.status === 401) {
            localStorage.removeItem("token");
            localStorage.removeItem("user");
            window.location.href = "/login";
        }
        return Promise.reject(error);
    },
);

// ============ AUTH CLIENT ============
export const authService = {
    login: async (
        username: string,
        password: string,
    ): Promise<LoginResponse> => {
        const response = await api.post<LoginResponse>("/api/v1/auth/login", {
            username,
            password,
        });
        return response.data;
    },

    register: async (formData: FormData): Promise<LoginResponse> => {
        const response = await api.post<LoginResponse>(
            "/api/v1/auth/register",
            {
                username: formData.get("username"),
                password: formData.get("password"),
            },
        );
        return response.data;
    },

    logout: async (): Promise<void> => {
        await api.post("/api/v1/auth/logout");
    },
};

// ============ AUTH ADMIN ============
export const adminAuthService = {
    login: async (
        username: string,
        password: string,
    ): Promise<LoginResponse> => {
        const response = await api.post<LoginResponse>("/api/v2/admin/login", {
            username,
            password,
        });
        return response.data;
    },

    logout: async (): Promise<void> => {
        await api.post("/api/v2/admin/logout");
    },
};

// ============ PRODUCTS (CLIENT) ============
export const productService = {
    getAll: async (): Promise<Product[]> => {
        const response = await api.get<ProductsResponse>("/api/v1/products");
        return response.data.products || [];
    },

    getById: async (id: number): Promise<Product> => {
        const response = await api.get<Product>(`/api/v1/products/${id}`);
        return response.data;
    },

    getByCategory: async (categoryId: number): Promise<Product[]> => {
        const response = await api.get<ProductsResponse>(
            `/api/v1/categories/${categoryId}/products`,
        );
        return response.data.products || [];
    },
};

// ============ CATEGORIES (CLIENT) ============
export const categoryService = {
    getAll: async (): Promise<Category[]> => {
        const response =
            await api.get<CategoriesResponse>("/api/v1/categories");
        return response.data.categories || [];
    },

    getById: async (id: number): Promise<Category> => {
        const response = await api.get<Category>(`/api/v1/categories/${id}`);
        return response.data;
    },
};

// ============ PROFILE (CLIENT) ============
export const profileService = {
    get: async (): Promise<Client> => {
        const response = await api.get<Client>("/api/v1/profile");
        return response.data;
    },

    updatePassword: async (newPassword: string): Promise<void> => {
        await api.put("/api/v1/profile", { new_password: newPassword });
    },

    updateUsername: async (username: string): Promise<Client> => {
        const response = await api.put<{ client: Client }>("/api/v1/profile", {
            username,
        });
        return response.data.client;
    },
};

// ============ ADMIN - CATEGORIES ============
export const adminCategoryService = {
    getAll: async (): Promise<Category[]> => {
        const response = await api.get<CategoriesResponse>(
            "/api/v2/admin/categories",
        );
        return response.data.categories || [];
    },

    create: async (data: CreateCategoryRequest): Promise<Category> => {
        const response = await api.post<Category>(
            "/api/v2/admin/categories",
            data,
        );
        return response.data;
    },

    update: async (id: number, data: UpdateCategoryRequest): Promise<void> => {
        await api.put(`/api/v2/admin/categories/${id}`, data);
    },

    delete: async (id: number): Promise<void> => {
        await api.delete(`/api/v2/admin/categories/${id}`);
    },
};

// ============ ADMIN - PRODUCTS ============
export const adminProductService = {
    getAll: async (): Promise<Product[]> => {
        const response = await api.get<ProductsResponse>(
            "/api/v2/admin/products",
        );
        return response.data.products || [];
    },

    getById: async (id: number): Promise<Product> => {
        const response = await api.get<Product>(`/api/v2/admin/products/${id}`);
        return response.data;
    },

    create: async (data: CreateProductRequest): Promise<Product> => {
        const response = await api.post<Product>(
            "/api/v2/admin/products",
            data,
        );
        return response.data;
    },

    update: async (id: number, data: UpdateProductRequest): Promise<void> => {
        await api.put(`/api/v2/admin/products/${id}`, data);
    },

    delete: async (id: number): Promise<void> => {
        await api.delete(`/api/v2/admin/products/${id}`);
    },

    addMedia: async (productId: number, file: File): Promise<void> => {
        const formData = new FormData();
        formData.append("file", file);
        await api.post(`/api/v2/admin/products/${productId}/media`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
    },

    deleteMedia: async (productId: number, mediaId: number): Promise<void> => {
        await api.delete(
            `/api/v2/admin/products/${productId}/media/${mediaId}`,
        );
    },

    setPrimaryMedia: async (
        productId: number,
        mediaId: number,
    ): Promise<void> => {
        await api.put(
            `/api/v2/admin/products/${productId}/media/${mediaId}/primary`,
        );
    },
};

// ============ ADMIN - CLIENTS ============
export const adminClientService = {
    getAll: async (): Promise<Client[]> => {
        const response = await api.get<ClientsResponse>(
            "/api/v2/admin/clients",
        );
        return response.data.clients || [];
    },

    getById: async (id: number): Promise<Client> => {
        const response = await api.get<Client>(`/api/v2/admin/clients/${id}`);
        return response.data;
    },

    delete: async (id: number): Promise<void> => {
        await api.delete(`/api/v2/admin/clients/${id}`);
    },

    approve: async (id: number): Promise<void> => {
        await api.put(`/api/v2/admin/clients/${id}/approve`);
    },

    reject: async (id: number): Promise<void> => {
        await api.put(`/api/v2/admin/clients/${id}/reject`);
    },
};

// ============ PANIER (CLIENT) ============
export const basketService = {
    add: async (item: BasketItem): Promise<BasketItem> => {
        const response = await api.post<BasketItem>("/api/v1/basket/add", item);
        return response.data;
    },

    getByUser: async (userId: number): Promise<BasketItem[]> => {
        const response = await api.get<BasketItem[]>(`/api/v1/all/basket/${userId}`);
        return response.data || [];
    },
};

// ============ COMMANDES (CLIENT) ============
export const commandService = {
    create: async (data: CommandRequest): Promise<Command> => {
        const response = await api.post<Command>("/api/v1/commands", data);
        return response.data;
    },

    getById: async (id: number): Promise<Command> => {
        const response = await api.get<Command>(`/api/v1/commands/${id}`);
        return response.data;
    },

    getByUsername: async (username: string): Promise<Command[]> => {
        const response = await api.get<Command[]>(`/api/v1/all/commands/${username}`);
        return response.data;
    },

    delete: async (id: number): Promise<void> => {
        await api.delete(`/api/v1/delete/commands/${id}`);
    },
};

// ============ TELEGRAM (ADMIN) ============
export const telegramService = {
    add: async (token: string, chat_id: string): Promise<void> => {
        await api.post("/api/v2/admin/add/telegram-credential", { token, chat_id });
    },

    getAll: async (): Promise<{ token: string; chat_id: string }[]> => {
        const response = await api.get<{ tokens: { token: string; chat_id: string }[] }>(
            "/api/v2/admin/telegram-credentials"
        );
        return response.data.tokens || [];
    },
};

// ============ COMMANDES (ADMIN) ============
export const adminCommandService = {
    getAll: async (): Promise<Command[]> => {
        const response = await api.get<Command[]>("/api/v2/admin/all/commands");
        return response.data;
    },
};

export default api;
